
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { logError } from "@/utils/sessionManager";
import { secureSignOut } from "@/utils/authUtils";

export const useAdminDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate("/admin");
          toast({
            title: "Authentication Required",
            description: "Please log in to access the admin panel",
            variant: "destructive",
          });
          return;
        }

        setCurrentUserId(session.user.id);

        // Check if user has super_admin role
        const { data: hasSuperAdminRole } = await supabase.rpc(
          'has_role',
          { 
            user_id: session.user.id,
            role: 'super_admin'
          }
        );
        
        setIsSuperAdmin(!!hasSuperAdminRole);
      } catch (error) {
        logError(error, "Checking authentication");
        navigate("/admin");
      }
    };
    
    checkAuth();
  }, [navigate, toast]);

  // Fetch admin profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['adminProfile'],
    queryFn: async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No authenticated user found");

        const { data, error } = await supabase
          .from('admin_profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (error) throw error;
        
        if (!data) return null;
        
        return {
          ...data,
          sweden_arrival_date: data.sweden_arrival_date ? new Date(data.sweden_arrival_date) : null,
          leadership_confidence: typeof data.leadership_confidence === 'string' 
            ? JSON.parse(data.leadership_confidence)
            : data.leadership_confidence
        };
      } catch (error) {
        console.error('Error fetching profile:', error);
        throw error;
      }
    },
  });

  // Fetch linked accounts (bidirectional)
  const { data: linkedAccounts } = useQuery({
    queryKey: ['linkedAccounts', currentUserId],
    queryFn: async () => {
      if (!currentUserId) return [];
      
      try {
        // Fetch links where current user is on either side
        const { data, error } = await supabase
          .from('account_links')
          .select('user_id, linked_user_id')
          .or(`user_id.eq.${currentUserId},linked_user_id.eq.${currentUserId}`);

        if (error) throw error;
        
        // Extract the "other" user's ID from each link
        const otherUserIds = [...new Set(
          (data || []).map(link => 
            link.user_id === currentUserId ? link.linked_user_id : link.user_id
          )
        )];

        if (otherUserIds.length === 0) return [];

        // Look up names from admin_profiles
        const { data: profiles } = await supabase
          .from('admin_profiles')
          .select('id, full_name')
          .in('id', otherUserIds);

        return otherUserIds.map(uid => ({
          userId: uid,
          fullName: profiles?.find(p => p.id === uid)?.full_name || uid,
        }));
      } catch (error) {
        logError(error, "Fetching linked accounts");
        return [];
      }
    },
    enabled: !!currentUserId,
  });

  // Fetch survey responses
  const { data: surveyResponses } = useQuery({
    queryKey: ['groupLeaderResponses'],
    queryFn: async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("No session found");

        const { data, error } = await supabase
          .from('group_leader_responses')
          .select('*')
          .eq('group_leader_id', session.user.id);

        if (error) throw error;
        return data;
      } catch (error) {
        logError(error, "Fetching survey responses");
        throw error;
      }
    },
  });

  const handleSignOut = async () => {
    try {
      const success = await secureSignOut();
      if (success) {
        // Force a complete page reload to ensure clean state
        window.location.href = "/admin";
      } else {
        throw new Error("Sign out failed");
      }
    } catch (error) {
      logError(error, "Sign out");
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    profile,
    profileLoading,
    surveyResponses,
    handleSignOut,
    isSuperAdmin,
    currentUserId,
    linkedAccounts
  };
};
